'use client';

import { c as _c } from "react/compiler-runtime";
import type { Role } from '@svs/nova-core/nextjs/auth';
import { useCouponGroupedOutcomes, useCouponId, useCouponPrice, useCouponStatus, useCouponWallets, useShareableCouponUrl } from '@svs/nova-poolbetting/core/nextjs/coupon';
import { useDraw } from '@svs/nova-poolbetting/core/nextjs/draw';
import { useEngine } from '@svs/nova-poolbetting/core/nextjs/engine';
import { useBalance } from '@svs/nova-poolbetting/core/nextjs/lib/client';
import type { CouponIndex } from '@svs/nova-poolbetting/schema/coupon';
import type { DrawNumber } from '@svs/nova-poolbetting/schema/draw';
import type { Definition } from '@svs/nova-poolbetting/schema/frameworks';
import type { RetailerBalanceResult } from '@svs/nova-poolbetting/schema/marketplace';
import type { ReactNode } from 'react';
import CouponOverview from './CouponOverview';
import CouponOverviewRowContainer from './CouponOverviewRowContainer';
type CouponOverviewContainerProps = {
  couponIndex?: CouponIndex;
  drawNumber: DrawNumber;
  product: Definition['drawProducts'][number];
  retailerData?: RetailerBalanceResult;
  role: Role;
  hasMultipleCoupons?: boolean;
};

/**
 * Asynchronously fetches and renders the coupon overview for a given draw number and product.
 *
 * @param root0 Component props
 * @param root0.couponIndex Coupon index to load
 * @param root0.drawNumber Draw number to load coupon from
 * @param root0.product Product to load coupon from
 * @param root0.retailerData Retailer balance data
 * @param root0.role Role of the user (e.g., 'Retailer', 'Player')
 * @param root0.hasMultipleCoupons Flag indicating whether multiple coupons are available, which can affect the UI rendering
 * @returns ReactNode representing the coupon overview, or undefined if the draw is not found.
 */
export default function CouponOverviewContainer(t0) {
  const $ = _c(40);
  const {
    couponIndex,
    drawNumber,
    product,
    retailerData,
    role,
    hasMultipleCoupons
  } = t0;
  const [couponId, currentCouponIndex] = useCouponId();
  const t1 = couponIndex ?? currentCouponIndex;
  let t2;
  if ($[0] !== couponId || $[1] !== t1) {
    t2 = {
      couponId,
      couponIndex: t1
    };
    $[0] = couponId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const couponStatus = useCouponStatus(t2);
  const definition = useEngine();
  const t3 = couponIndex ?? currentCouponIndex;
  let t4;
  if ($[3] !== couponId || $[4] !== t3) {
    t4 = {
      couponId,
      couponIndex: t3
    };
    $[3] = couponId;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const outcomeGroups = useCouponGroupedOutcomes(t4);
  const fullHedgeCount = outcomeGroups[3] ?? 0;
  const halfHedgeCount = outcomeGroups[2] ?? 0;
  const t5 = couponIndex ?? currentCouponIndex;
  let t6;
  if ($[6] !== couponId || $[7] !== t5) {
    t6 = {
      couponId,
      couponIndex: t5
    };
    $[6] = couponId;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const price = useCouponPrice(t6);
  const t7 = couponIndex ?? currentCouponIndex;
  let t8;
  if ($[9] !== couponId || $[10] !== drawNumber || $[11] !== product || $[12] !== t7) {
    t8 = {
      product,
      couponId,
      boardIndex: 0,
      couponIndex: t7,
      drawNumber
    };
    $[9] = couponId;
    $[10] = drawNumber;
    $[11] = product;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const url = useShareableCouponUrl(t8);
  let t9;
  if ($[14] !== drawNumber || $[15] !== product) {
    t9 = {
      drawNumber,
      product
    };
    $[14] = drawNumber;
    $[15] = product;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const drawData = useDraw(t9);
  const fallbackEventType = definition.eventType;
  let t10;
  if ($[17] !== couponId) {
    t10 = {
      couponId
    };
    $[17] = couponId;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const activeWallets = useCouponWallets(t10);
  const t11 = couponIndex ?? currentCouponIndex;
  let t12;
  if ($[19] !== activeWallets || $[20] !== drawNumber || $[21] !== product || $[22] !== retailerData || $[23] !== role || $[24] !== t11) {
    t12 = {
      activeWallets,
      couponIndex: t11,
      drawNumber,
      retailerData,
      product,
      role
    };
    $[19] = activeWallets;
    $[20] = drawNumber;
    $[21] = product;
    $[22] = retailerData;
    $[23] = role;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const {
    balance,
    shouldRedirectUser
  } = useBalance(t12);
  const t13 = couponIndex ?? currentCouponIndex;
  const t14 = couponStatus === "INVALID_NOT_STARTED";
  let t15;
  if ($[26] !== balance || $[27] !== drawData.data.bettingEvent || $[28] !== drawData.data.draw || $[29] !== drawData.data.participants || $[30] !== fallbackEventType || $[31] !== fullHedgeCount || $[32] !== halfHedgeCount || $[33] !== hasMultipleCoupons || $[34] !== price || $[35] !== shouldRedirectUser || $[36] !== t13 || $[37] !== t14 || $[38] !== url) {
    t15 = <CouponOverview couponIndex={t13} draw={drawData.data.draw} drawParticipants={drawData.data.participants} bettingEvents={drawData.data.bettingEvent} defaultEventType={fallbackEventType} isDisabled={t14} url={url} balance={balance} price={price} fullHedgeCount={fullHedgeCount} halfHedgeCount={halfHedgeCount} shouldRedirectUser={shouldRedirectUser} hasMultipleCoupons={hasMultipleCoupons} CouponOverviewRow={CouponOverviewRowContainer} />;
    $[26] = balance;
    $[27] = drawData.data.bettingEvent;
    $[28] = drawData.data.draw;
    $[29] = drawData.data.participants;
    $[30] = fallbackEventType;
    $[31] = fullHedgeCount;
    $[32] = halfHedgeCount;
    $[33] = hasMultipleCoupons;
    $[34] = price;
    $[35] = shouldRedirectUser;
    $[36] = t13;
    $[37] = t14;
    $[38] = url;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  return t15;
}